import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AdminButton,
  AdminEmptyState,
  AdminPageLayout,
  AdminSearchInput,
  AdminSelect,
  AdminStatusBadge,
  AdminTable,
  AdminTd,
  AdminTextarea,
  AdminTr,
} from "@/components/admin";
import {
  answerAdminCustomerInquiry,
  fetchAdminCustomerInquiries,
  fetchAdminCustomerInquiryDetail,
  updateAdminCustomerInquiryStatus,
} from "@/lib/api";
import { formatDateTime, formatNullable } from "@/lib/format";
import type { AdminCustomerInquiryDetail, AdminCustomerInquiryItem } from "@/types";

import PublicDeletionInquiryPanel from "./PublicDeletionInquiryPanel";

type InquiryFilter = "all" | "RECEIVED" | "IN_PROGRESS" | "ANSWERED" | "CLOSED";

const statusLabels: Record<string, string> = {
  RECEIVED: "접수",
  IN_PROGRESS: "처리 중",
  ANSWERED: "답변 완료",
  CLOSED: "종료",
};

const statusTones: Record<string, "orange" | "blue" | "emerald" | "slate"> = {
  RECEIVED: "orange",
  IN_PROGRESS: "blue",
  ANSWERED: "emerald",
  CLOSED: "slate",
};

const typeLabels: Record<string, string> = {
  PRIVACY_DELETION: "계정·데이터 삭제",
  SERVICE: "서비스",
  ACCOUNT: "계정",
  ALERT: "알림",
  REPORT: "리포트",
  ETC: "기타",
};

const summaryIconClass: Record<string, string> = {
  blue: "bg-blue-50 text-blue-600",
  orange: "bg-orange-50 text-orange-600",
  emerald: "bg-emerald-50 text-emerald-600",
};

const filterOptions: { key: InquiryFilter; label: string }[] = [
  { key: "all", label: "전체" },
  { key: "RECEIVED", label: "접수" },
  { key: "IN_PROGRESS", label: "처리 중" },
  { key: "ANSWERED", label: "답변 완료" },
  { key: "CLOSED", label: "종료" },
];

const isPendingInquiry = (item: AdminCustomerInquiryItem) => {
  return item.status === "RECEIVED" || item.status === "IN_PROGRESS";
};

const CustomerInquiriesPage = () => {
  const [items, setItems] = useState<AdminCustomerInquiryItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedInquiry, setSelectedInquiry] = useState<AdminCustomerInquiryDetail | null>(null);
  const [activeFilter, setActiveFilter] = useState<InquiryFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [answerContent, setAnswerContent] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reasonCode, setReasonCode] = useState("OPERATOR_REQUEST");
  const [reason, setReason] = useState("");
  const [auditMessage, setAuditMessage] = useState("");

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setErrorMessage("");
    try {
      const nextItems = await fetchAdminCustomerInquiries({
        status: activeFilter === "all" ? undefined : activeFilter,
        keyword: searchQuery.trim() || undefined,
      });
      setItems(nextItems);
      setSelectedId((current) =>
        current != null && nextItems.some((item) => item.inquiryId === current)
          ? current
          : nextItems[0]?.inquiryId ?? null,
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "1:1 문의 목록을 불러오지 못했습니다.");
    } finally {
      setLoadingList(false);
    }
  }, [activeFilter, searchQuery]);

  const loadDetail = useCallback(async (inquiryId: number) => {
    setLoadingDetail(true);
    setErrorMessage("");
    try {
      const detail = await fetchAdminCustomerInquiryDetail(inquiryId);
      setSelectedInquiry(detail);
      setAnswerContent(detail.answerContent ?? "");
    } catch (error) {
      setSelectedInquiry(null);
      setAnswerContent("");
      setErrorMessage(error instanceof Error ? error.message : "1:1 문의 상세를 불러오지 못했습니다.");
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId == null) {
      setSelectedInquiry(null);
      setAnswerContent("");
      return;
    }
    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const summary = useMemo(() => {
    const pending = items.filter(isPendingInquiry).length;
    const answered = items.filter((item) => item.status === "ANSWERED").length;
    return { total: items.length, pending, answered };
  }, [items]);

  const handleAnswer = async () => {
    if (!selectedId || !answerContent.trim()) {
      return;
    }
    setSaving(true);
    setErrorMessage("");
    try {
      const result = await answerAdminCustomerInquiry(selectedId, answerContent, reasonCode, reason);
      const detail = result.inquiry;
      setSelectedInquiry(detail);
      setAnswerContent(detail.answerContent ?? "");
      setAuditMessage(`감사 #${result.audit.auditLogId} · 답변 저장 · ${result.audit.beforeStatus} → ${result.audit.afterStatus}`);
      setReason("");
      await loadList();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "답변 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleStartProgress = async () => {
    if (!selectedId) {
      return;
    }
    setSaving(true);
    try {
      const result = await updateAdminCustomerInquiryStatus(selectedId, "IN_PROGRESS", reasonCode, reason);
      const detail = result.inquiry;
      setSelectedInquiry(detail);
      setAuditMessage(`감사 #${result.audit.auditLogId} · 상태 변경 · ${result.audit.beforeStatus} → ${result.audit.afterStatus}`);
      setReason("");
      await loadList();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "문의 상태 변경에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageLayout
      title="1:1 문의"
      subtitle={`총 ${summary.total}건 · 처리 필요 ${summary.pending}건`}
      actions={
        <AdminButton variant="soft" size="sm" icon="ri-refresh-line" onClick={() => void loadList()} disabled={loadingList}>
          새로고침
        </AdminButton>
      }
    >
      {errorMessage && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
          {errorMessage}
        </div>
      )}

      <section className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { label: "전체 문의", value: summary.total, icon: "ri-question-answer-line", tone: "blue" },
          { label: "처리 필요", value: summary.pending, icon: "ri-alarm-warning-line", tone: "orange" },
          { label: "답변 완료", value: summary.answered, icon: "ri-check-double-line", tone: "emerald" },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-500">{card.label}</span>
              <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${summaryIconClass[card.tone]}`}>
                <i className={`${card.icon} text-base`} />
              </span>
            </div>
            <div className="mt-2 text-2xl font-bold text-slate-900">{card.value}건</div>
          </div>
        ))}
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <section className="xl:col-span-3 bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <AdminSearchInput value={searchQuery} onChange={setSearchQuery} placeholder="제목, 내용, 이메일, 문의자 검색" className="flex-1" />
            <div className="flex flex-wrap gap-1.5" aria-label="문의 상태 필터">
              {filterOptions.map((option) => (
                <AdminButton
                  key={option.key}
                  type="button"
                  variant={activeFilter === option.key ? "soft" : "secondary"}
                  size="sm"
                  onClick={() => setActiveFilter(option.key)}
                  aria-pressed={activeFilter === option.key}
                  className="rounded-md"
                >
                  {option.label}
                </AdminButton>
              ))}
            </div>
          </div>

          <AdminTable
            headers={["유형", "제목", "문의자", "이메일", "접수 시각", "상태"]}
            loading={loadingList}
            loadingMessage="1:1 문의 목록을 불러오는 중입니다."
            emptyMessage={!loadingList && items.length === 0 ? "현재 조건에 맞는 1:1 문의가 없습니다." : undefined}
          >
            {items.map((item) => (
              <AdminTr
                key={item.inquiryId}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedId(item.inquiryId)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedId(item.inquiryId);
                  }
                }}
                aria-selected={selectedId === item.inquiryId}
                className={`cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${
                  selectedId === item.inquiryId ? "bg-blue-50/70" : "hover:bg-slate-50"
                }`}
              >
                <AdminTd className="whitespace-nowrap">
                  <AdminStatusBadge label={typeLabels[item.inquiryType] ?? item.inquiryType} tone="blue" />
                </AdminTd>
                <AdminTd className="min-w-48">
                  <span className="block max-w-80 truncate text-sm font-semibold text-slate-900">{item.title}</span>
                </AdminTd>
                <AdminTd className="whitespace-nowrap text-xs text-slate-600">{item.requesterLabel}</AdminTd>
                <AdminTd className="max-w-52 truncate text-xs text-slate-500">{formatNullable(item.contactEmail)}</AdminTd>
                <AdminTd className="whitespace-nowrap text-xs text-slate-500">{formatDateTime(item.createdAt)}</AdminTd>
                <AdminTd className="whitespace-nowrap">
                  <AdminStatusBadge label={statusLabels[item.status] ?? item.status} tone={statusTones[item.status] ?? "slate"} />
                </AdminTd>
              </AdminTr>
            ))}
          </AdminTable>
        </section>

        <aside className="xl:col-span-1 bg-white rounded-lg border border-slate-200 shadow-sm p-4 h-fit">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">문의 상세</h3>
            {selectedInquiry && (
              <AdminStatusBadge label={statusLabels[selectedInquiry.status] ?? selectedInquiry.status} tone={statusTones[selectedInquiry.status] ?? "slate"} />
            )}
          </div>

          {loadingDetail ? (
            <div className="text-sm text-slate-500">상세를 불러오는 중입니다.</div>
          ) : !selectedInquiry ? (
            <AdminEmptyState icon="ri-file-search-line" title="선택된 문의가 없습니다" description="목록에서 문의를 선택해 주세요." />
          ) : selectedInquiry.inquiryType === "PRIVACY_DELETION" ? (
            <PublicDeletionInquiryPanel key={selectedInquiry.inquiryId} inquiryId={selectedInquiry.inquiryId}
              onChanged={async () => { await loadList(); await loadDetail(selectedInquiry.inquiryId); }} />
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-xs text-slate-400 mb-1">제목</div>
                <div className="text-sm font-semibold text-slate-900">{selectedInquiry.title}</div>
              </div>
              <div className="grid grid-cols-1 gap-3 text-xs sm:grid-cols-2">
                <div className="min-w-0">
                  <div className="text-slate-400 mb-1">문의자</div>
                  <div className="break-all font-medium text-slate-700">{selectedInquiry.requesterLabel}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-slate-400 mb-1">유형</div>
                  <div className="break-words font-medium text-slate-700">{typeLabels[selectedInquiry.inquiryType] ?? selectedInquiry.inquiryType}</div>
                </div>
                <div className="min-w-0 sm:col-span-2">
                  <div className="text-slate-400 mb-1">이메일</div>
                  <div className="break-all font-medium text-slate-700">{formatNullable(selectedInquiry.contactEmail)}</div>
                </div>
              </div>
              <div className="space-y-2 border-t border-slate-100 pt-3">
                <div className="text-xs text-slate-400">변경 사유</div>
                <AdminSelect value={reasonCode} onValueChange={setReasonCode} disabled={saving}>
                  <option value="OPERATOR_REQUEST">운영자 요청</option><option value="DATA_CORRECTION">데이터 정정</option><option value="SECURITY_RESPONSE">보안 대응</option><option value="TEST_ROLLBACK">테스트 원복</option><option value="OTHER">기타</option>
                </AdminSelect>
                <AdminTextarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} rows={3} placeholder="처리 사유를 10자 이상 입력하세요." disabled={saving} />
                {auditMessage && <p className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{auditMessage}</p>}
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">문의 내용</div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {selectedInquiry.content}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs text-slate-400">관리자 답변</div>
                  {selectedInquiry.status === "RECEIVED" && (
                    <AdminButton type="button" variant="ghost" size="sm" onClick={() => void handleStartProgress()} disabled={saving || reason.trim().length < 10} className="h-auto px-1 py-0 text-blue-600">
                      처리 중으로 변경
                    </AdminButton>
                  )}
                </div>
                <AdminTextarea
                  value={answerContent}
                  onChange={(event) => setAnswerContent(event.target.value)}
                  rows={8}
                  maxLength={2000}
                  placeholder="고객에게 전달할 답변을 입력하세요."
                  className="p-3 text-sm"
                />
                <div className="mt-1 flex items-center justify-between text-xs text-slate-400">
                  <span>{formatDateTime(selectedInquiry.answeredAt)}</span>
                  <span>{answerContent.length}/2000</span>
                </div>
              </div>
              <AdminButton
                variant="primary"
                size="sm"
                icon="ri-send-plane-line"
                onClick={() => void handleAnswer()}
                disabled={saving || !answerContent.trim() || reason.trim().length < 10}
                className="w-full justify-center"
              >
                {saving ? "저장 중" : "답변 저장"}
              </AdminButton>
            </div>
          )}
        </aside>
      </div>
    </AdminPageLayout>
  );
};

export default CustomerInquiriesPage;
